import React, { useEffect, useState } from "react";

import BugApi from "../../api/BugApi";

import { showError, showSuccess } from "../../utils/toastUtils";

function MyBugs() {
  const [bugs, setBugs] = useState([]);

  const [loading, setLoading] = useState(true);

  const [actionLoading, setActionLoading] = useState(null);

  const [confirmBugId, setConfirmBugId] = useState(null);

  /* =========================================================
     BACKEND URL
  ========================================================= */

  const API_BASE = process.env.REACT_APP_SOCKET_URL || "http://localhost:5000";

  /* =========================================================
     FILE URL
  ========================================================= */

  const getFileUrl = (filePath) => {
    if (!filePath) {
      return "";
    }

    /* Already complete URL */
    if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
      return filePath;
    }

    const baseUrl = API_BASE.replace(/\/+$/, "");

    /*
      Remove all leading slashes first.

      Example:
      /uploads/bugs/image.jpg
      //uploads/bugs/image.jpg

      Both become:
      uploads/bugs/image.jpg
    */

    const cleanPath = String(filePath).replace(/^\/+/, "");

    return `${baseUrl}/${cleanPath}`;
  };

  /* =========================================================
     CHECK IMAGE
  ========================================================= */

  const isImage = (filePath) => {
    return /\.(jpg|jpeg|png|webp|gif)$/i.test(filePath || "");
  };

  /* =========================================================
     CHECK VIDEO
  ========================================================= */

  const isVideo = (filePath) => {
    return /\.(mp4|webm|mov|avi|mkv|ogg)$/i.test(filePath || "");
  };

  /* =========================================================
     FETCH BUGS
  ========================================================= */

  const loadBugs = async () => {
    try {
      setLoading(true);

      const res = await BugApi.getAssignedBugs();

      if (res?.code === 200) {
        setBugs(Array.isArray(res.data) ? res.data : []);
      } else {
        setBugs([]);
      }
    } catch (error) {
      console.error("Failed to load assigned bugs:", error);

      showError(error?.message || "Failed to load assigned bugs");

      setBugs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBugs();
  }, []);

  /* =========================================================
     START BUG WORK
  ========================================================= */

  const startWork = async (id) => {
    try {
      setActionLoading(id);

      const res = await BugApi.startBugWork(id);

      if (res?.code === 200) {
        showSuccess("Bug work started successfully");

        await loadBugs();
      }
    } catch (error) {
      showError(error?.message || "Unable to start bug");
    } finally {
      setActionLoading(null);
    }
  };

  /* =========================================================
     MARK BUG FIXED
  ========================================================= */

  const markFixed = async (id) => {
    try {
      setActionLoading(id);

      const res = await BugApi.fixBug(id);

      if (res?.code === 200) {
        showSuccess("Bug marked as fixed. Waiting for tester retest.");

        await loadBugs();
      } else {
        showError(res?.message || "Unable to mark bug fixed");
      }
    } catch (error) {
      console.error("MARK FIXED ERROR:", error);

      showError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to mark bug fixed",
      );
    } finally {
      setActionLoading(null);
      setConfirmBugId(null);
    }
  };

  /* =========================================================
     STATUS BADGE
  ========================================================= */

  const getStatusBadge = (status) => {
    const map = {
      open: "danger",
      in_progress: "primary",
      fixed: "warning",
      reopened: "danger",
      closed: "success",
    };

    return (
      <span className={`badge bg-${map[status] || "secondary"}`}>
        {status === "in_progress"
          ? "In Progress"
          : status === "fixed"
            ? "Waiting For Retest"
            : status === "reopened"
              ? "Reopened"
              : status === "closed"
                ? "Verified"
                : "Open"}
      </span>
    );
  };

  /* =========================================================
     ATTACHMENTS
  ========================================================= */

  const renderAttachments = (bug) => {
    const attachments = Array.isArray(bug?.attachments) ? bug.attachments : [];

    if (attachments.length === 0) {
      return <span className="text-muted small">No attachment</span>;
    }

    return (
      <div
        className="d-flex flex-wrap align-items-center gap-2"
        style={{
          maxWidth: "120px",
        }}
      >
        {attachments.map((filePath, index) => {
          const fileUrl = getFileUrl(filePath);

          /* =================================================
               IMAGE
            ================================================= */

          if (isImage(filePath)) {
            return (
              <a
                key={index}
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Open full image"
                style={{
                  display: "block",
                  width: "70px",
                  height: "50px",
                  flexShrink: 0,
                }}
              >
                <img
                  src={fileUrl}
                  alt={`Bug attachment ${index + 1}`}
                  style={{
                    width: "70px",
                    height: "50px",
                    objectFit: "cover",
                    borderRadius: "5px",
                    border: "1px solid #dee2e6",
                    display: "block",
                    cursor: "pointer",
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </a>
            );
          }

          /* =================================================
               VIDEO
            ================================================= */

          if (isVideo(filePath)) {
            return (
              <a
                key={index}
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary"
                style={{
                  fontSize: "11px",
                  padding: "4px 7px",
                  whiteSpace: "nowrap",
                }}
                title="Open screen recording"
              >
                🎥 View Video
              </a>
            );
          }

          /* =================================================
               OTHER FILE
            ================================================= */

          return (
            <a
              key={index}
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-outline-secondary"
              style={{
                fontSize: "11px",
                padding: "4px 7px",
                whiteSpace: "nowrap",
              }}
            >
              📎 Open
            </a>
          );
        })}
      </div>
    );
  };

  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {
    return (
      <div className="content">
        <div className="text-center py-5">Loading assigned bugs...</div>
      </div>
    );
  }

  /* =========================================================
     UI
  ========================================================= */

  return (
    <div className="content">
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          {/* =================================================
              HEADER
          ================================================= */}

          <div
            className="
              d-flex
              justify-content-between
              align-items-center
              mb-4
            "
          >
            <div>
              <h4 className="mb-1">My Assigned Bugs</h4>

              <p className="text-muted mb-0">Bugs assigned to you for fixing</p>
            </div>

            <span className="badge bg-danger">{bugs.length} Bugs</span>
          </div>

          {/* =================================================
              TABLE
          ================================================= */}

          <div className="table-responsive">
            <table
              className="
                table
                table-hover
                align-middle
              "
            >
              <thead>
                <tr>
                  <th>Project</th>

                  <th>Bug</th>

                  <th>Test Case</th>

                  <th>Severity</th>

                  <th>Priority</th>

                  <th
                    style={{
                      width: "130px",
                    }}
                  >
                    Attachments
                  </th>

                  <th>Status</th>

                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {bugs.length > 0 ? (
                  bugs.map((bug) => (
                    <tr key={bug._id}>
                      {/* =================================================
                            PROJECT
                        ================================================= */}

                      <td>
                        <div className="fw-semibold">
                          {bug?.projectId?.projectName || "N/A"}
                        </div>
                      </td>

                      {/* =================================================
                            BUG
                        ================================================= */}

                      <td>
                        <div
                          className="fw-semibold"
                          style={{
                            maxWidth: "250px",
                          }}
                        >
                          {bug.title}
                        </div>

                        <small
                          className="text-muted"
                          style={{
                            display: "block",
                            maxWidth: "250px",
                          }}
                        >
                          {bug.actualResult || bug.description || "-"}
                        </small>
                      </td>

                      {/* =================================================
                            TEST CASE
                        ================================================= */}

                      <td>{bug?.testCaseId?.title || "-"}</td>

                      {/* =================================================
                            SEVERITY
                        ================================================= */}

                      <td>
                        <span className="badge bg-warning text-dark">
                          {bug.severity}
                        </span>
                      </td>

                      {/* =================================================
                            PRIORITY
                        ================================================= */}

                      <td>
                        <span className="badge bg-info">{bug.priority}</span>
                      </td>

                      {/* =================================================
                            ATTACHMENTS
                        ================================================= */}

                      <td>{renderAttachments(bug)}</td>

                      {/* =================================================
                            STATUS
                        ================================================= */}

                      <td>{getStatusBadge(bug.status)}</td>

                      {/* =================================================
                            ACTION
                        ================================================= */}

                      <td>
                        {["open", "reopened"].includes(bug.status) && (
                          <button
                            className="
                                btn
                                btn-primary
                                btn-sm
                                me-2
                              "
                            disabled={actionLoading === bug._id}
                            onClick={() => startWork(bug._id)}
                          >
                            {actionLoading === bug._id
                              ? "Starting..."
                              : "Start Work"}
                          </button>
                        )}

                        {["open", "reopened", "in_progress"].includes(
                          bug.status,
                        ) && (
                          <button
                            className="
                                btn
                                btn-success
                                btn-sm
                              "
                            disabled={actionLoading === bug._id}
                            onClick={() => setConfirmBugId(bug._id)}
                          >
                            {actionLoading === bug._id
                              ? "Updating..."
                              : "✓ Mark Fixed"}
                          </button>
                        )}

                        {bug.status === "fixed" && (
                          <span
                            className="
                                badge
                                bg-warning
                                text-dark
                              "
                          >
                            Waiting For Retest
                          </span>
                        )}

                        {bug.status === "closed" && (
                          <div>
                            <span
                              className="
                                  badge
                                  bg-success
                                "
                            >
                              ✓ Final Testing Passed
                            </span>

                            <div
                              className="
                                  small
                                  text-success
                                  mt-1
                                "
                            >
                              Verified by Tester
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="8"
                      className="
                        text-center
                        py-5
                        text-muted
                      "
                    >
                      <i
                        className="
                          ti
                          ti-bug-off
                          fs-1
                          d-block
                          mb-3
                        "
                      />
                      No bugs assigned to you.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {confirmBugId && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.55)",
          }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Confirm Bug Fix</h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setConfirmBugId(null)}
                />
              </div>

              <div className="modal-body">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-warning-subtle d-flex align-items-center justify-content-center"
                    style={{
                      width: "45px",
                      height: "45px",
                      minWidth: "45px",
                    }}
                  >
                    <i className="ti ti-alert-triangle text-warning fs-4" />
                  </div>

                  <div>
                    <h6 className="fw-semibold mb-1">
                      Mark this bug as fixed?
                    </h6>

                    <p className="text-muted mb-0">
                      Please confirm that you have fixed this issue. The bug
                      will then be sent to the tester for retesting.
                    </p>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setConfirmBugId(null)}
                  disabled={actionLoading === confirmBugId}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => markFixed(confirmBugId)}
                  disabled={actionLoading === confirmBugId}
                >
                  {actionLoading === confirmBugId
                    ? "Updating..."
                    : "Yes, Mark Fixed"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyBugs;
